import { useState } from 'react';
import type { ExerciseInput, ExerciseRow } from '@st/contract';
import { useSaveExercise, useTrainerExercises } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toMutationMessage } from '../../i18n/errorMessage';
import { Badge, Button, Chip, Input, Modal, Select, SkeletonList, useToast } from '../../ui';
import { ExerciseThumb } from '../../ui/ExerciseThumb';
import { CATEGORIES, CATEGORY_KEY, DIFFICULTY_KEY } from './builder/categories';

export function TrainerExercises() {
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const query = useTrainerExercises();
  const save = useSaveExercise();

  const [category, setCategory] = useState<string | null>(null);
  const [editing, setEditing] = useState<ExerciseRow | 'new' | null>(null);

  return (
    <>
      <PageHeader
        eyebrow={t('shell.trainer')}
        title={t('shell.exercises')}
        action={
          <Button size="sm" onClick={() => setEditing('new')}>
            {t('trainer.newExercise')}
          </Button>
        }
      />
      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={5} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{
          message: t('empty.noExercises'),
          actionLabel: t('trainer.newExercise'),
          onAction: () => setEditing('new'),
        }}
      >
        {(data) => {
          const categories = [...new Set(data.items.map((e) => e.category))].sort();
          const rows = category ? data.items.filter((e) => e.category === category) : data.items;
          return (
            <>
              <div className="mb-4 flex flex-wrap gap-2">
                <Chip selected={category === null} onClick={() => setCategory(null)}>
                  {t('trainer.all')}
                </Chip>
                {categories.map((c) => (
                  <Chip key={c} selected={category === c} onClick={() => setCategory(c)}>
                    {t(CATEGORY_KEY[c] ?? c)}
                  </Chip>
                ))}
              </div>
              <ul className="grid gap-3 sm:grid-cols-2">
                {rows.map((e) => (
                  <li
                    key={e.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-line-subtle bg-surface p-4 shadow-card"
                  >
                    <ExerciseThumb
                      src={e.imageUrl}
                      name={locale === 'en' ? e.nameEn : e.nameHr}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] text-ink">
                        {locale === 'en' ? e.nameEn : e.nameHr}
                      </span>
                      <span className="block text-[12px] text-ink-muted">
                        {t(CATEGORY_KEY[e.category] ?? e.category)}
                        {e.equipment ? ` · ${e.equipment}` : ''}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {e.videoKey && <Badge tone="accent">video</Badge>}
                      {e.trainerId === null ? (
                        <Badge tone="neutral">{t('exerciseLib.globalBadge')}</Badge>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setEditing(e)}>
                          {t('action.edit')}
                        </Button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          );
        }}
      </QueryBoundary>

      {editing && (
        <ExerciseFormModal
          exercise={editing === 'new' ? null : editing}
          pending={save.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(input) =>
            save.mutate(
              { ...(editing === 'new' ? {} : { id: editing.id }), input },
              {
                onSuccess: () => {
                  setEditing(null);
                  toast(t('builder.exerciseSaved'), 'success');
                },
                onError: (error) => toast(toMutationMessage(t, error), 'danger'),
              },
            )
          }
        />
      )}
    </>
  );
}

const SLUG_RE = /^[a-z0-9-]+$/;

function ExerciseFormModal({
  exercise,
  pending,
  onClose,
  onSubmit,
}: {
  exercise: ExerciseRow | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: ExerciseInput) => void;
}) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState(exercise?.slug ?? '');
  const [nameHr, setNameHr] = useState(exercise?.nameHr ?? '');
  const [nameEn, setNameEn] = useState(exercise?.nameEn ?? '');
  const [category, setCategory] = useState(exercise?.category ?? 'full_body');
  const [muscleGroups, setMuscleGroups] = useState((exercise?.muscleGroupsJson ?? []).join(', '));
  const [equipment, setEquipment] = useState(exercise?.equipment ?? '');
  const [loadType, setLoadType] = useState<ExerciseInput['loadType']>(
    exercise?.loadType ?? 'weighted',
  );
  const [difficulty, setDifficulty] = useState(exercise?.difficulty ?? '');
  const [errors, setErrors] = useState<{ slug?: string; name?: string }>({});

  function submit() {
    const next: { slug?: string; name?: string } = {};
    if (slug.trim().length < 2) next.slug = t('exerciseLib.slugRequired');
    else if (!SLUG_RE.test(slug.trim())) next.slug = t('exerciseLib.slugInvalid');
    if (!nameHr.trim() || !nameEn.trim()) next.name = t('exerciseLib.nameRequired');
    setErrors(next);
    if (next.slug || next.name) return;

    onSubmit({
      slug: slug.trim(),
      nameHr: nameHr.trim(),
      nameEn: nameEn.trim(),
      category: category as ExerciseInput['category'],
      muscleGroups: muscleGroups
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      equipment: equipment.trim() || null,
      loadType,
      difficulty: (difficulty || null) as ExerciseInput['difficulty'],
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={exercise ? t('exerciseLib.editTitle') : t('exerciseLib.newTitle')}
      closeLabel={t('a11y.closeDialog')}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" loading={pending} onClick={submit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          id="exercise-slug"
          label={t('exerciseLib.slug')}
          hint={t('exerciseLib.slugHint')}
          value={slug}
          required
          error={errors.slug}
          onChange={(e) => setSlug(e.target.value)}
        />
        <Input
          id="exercise-name-hr"
          label={t('exerciseLib.nameHr')}
          value={nameHr}
          required
          error={errors.name}
          onChange={(e) => setNameHr(e.target.value)}
        />
        <Input
          id="exercise-name-en"
          label={t('exerciseLib.nameEn')}
          value={nameEn}
          required
          onChange={(e) => setNameEn(e.target.value)}
        />
        <Select
          id="exercise-category"
          label={t('exerciseLib.category')}
          value={category}
          options={CATEGORIES.map((c) => ({ value: c, label: t(CATEGORY_KEY[c] ?? c) }))}
          onChange={(e) => setCategory(e.target.value)}
        />
        <Input
          id="exercise-groups"
          label={t('exerciseLib.muscleGroups')}
          hint={t('exerciseLib.muscleGroupsHint')}
          value={muscleGroups}
          onChange={(e) => setMuscleGroups(e.target.value)}
        />
        <Input
          id="exercise-equipment"
          label={t('exerciseLib.equipment')}
          value={equipment}
          onChange={(e) => setEquipment(e.target.value)}
        />
        <Select
          id="exercise-load-type"
          label={t('exerciseLib.loadType')}
          hint={t('exerciseLib.loadTypeHint')}
          value={loadType}
          options={[
            { value: 'weighted', label: t('exerciseLib.loadWeighted') },
            { value: 'bodyweight', label: t('exerciseLib.loadBodyweight') },
            { value: 'timed', label: t('exerciseLib.loadTimed') },
          ]}
          onChange={(e) => setLoadType(e.target.value as ExerciseInput['loadType'])}
        />
        <Select
          id="exercise-difficulty"
          label={t('exerciseLib.difficulty')}
          value={difficulty}
          options={[
            { value: '', label: t('action.optional') },
            ...Object.entries(DIFFICULTY_KEY).map(([value, key]) => ({
              value,
              label: t(key),
            })),
          ]}
          onChange={(e) => setDifficulty(e.target.value as ExerciseRow['difficulty'] & string)}
        />
      </form>
    </Modal>
  );
}
